import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, PersonAdd as PersonAddIcon, Refresh as RefreshIcon, Settings as SettingsIcon } from '@mui/icons-material';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useSnackbar } from 'notistack';
import Can from '../../components/Can';
import VendedorSelect from '../../components/VendedorSelect';
import { ROUTES } from '../../config/routes';
import crmContactoService from '../../services/crmContactoService';
import crmOportunidadService from '../../services/crmOportunidadService';
import crmPipelineService from '../../services/crmPipelineService';
import type {
  CrmContactoListItem,
  CrmKanban,
  CrmKanbanColumna,
  CrmOportunidadListItem,
  CrmPipeline,
} from '../../types/crm';
import { formatCurrency } from '../../utils/formatters';
import { getApiErrorMessage } from '../../utils/apiError';
import CrmDealCard from './components/CrmDealCard';

const KanbanColumn: React.FC<{
  columna: CrmKanbanColumna;
  onDealClick: (deal: CrmOportunidadListItem) => void;
  onAddClick?: (etapaId: number) => void;
}> = ({ columna, onDealClick, onAddClick }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `etapa-${columna.etapa.id}`,
    data: { etapaId: columna.etapa.id },
  });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        minWidth: { xs: 240, sm: 280 },
        maxWidth: { xs: 280, sm: 320 },
        flex: { xs: '0 0 240px', sm: '0 0 280px' },
        bgcolor: isOver ? 'action.hover' : '#f0f2f5',
        borderRadius: 2,
        p: 1.5,
        display: 'flex',
        flexDirection: 'column',
        maxHeight: { xs: 'calc(100vh - 320px)', sm: 'calc(100vh - 260px)' },
        border: isOver ? '2px solid' : '1px solid transparent',
        borderColor: isOver ? 'primary.main' : 'transparent',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              flexShrink: 0,
              bgcolor: columna.etapa.color || '#1976d2',
            }}
          />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
            {columna.etapa.nombre}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
            {columna.oportunidades.length}
          </Typography>
        </Stack>
        {onAddClick && (
          <Can permiso="crm.oportunidades.nuevo">
            <IconButton
              size="small"
              aria-label={`Nueva oportunidad en ${columna.etapa.nombre}`}
              onClick={(e) => {
                e.stopPropagation();
                onAddClick(columna.etapa.id);
              }}
              sx={{ p: 0.35, flexShrink: 0 }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Can>
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
        {formatCurrency(columna.montoTotal)}
      </Typography>
      <Box sx={{ overflowY: 'auto', flex: 1, pr: 0.5 }}>
        {columna.oportunidades.map((deal) => (
          <CrmDealCard key={deal.id} deal={deal} onClick={() => onDealClick(deal)} />
        ))}
      </Box>
    </Box>
  );
};

const CrmPipelineKanban: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [pipelines, setPipelines] = useState<CrmPipeline[]>([]);
  const [pipelineId, setPipelineId] = useState<number | ''>('');
  const [nroVendedor, setNroVendedor] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [kanban, setKanban] = useState<CrmKanban | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeDeal, setActiveDeal] = useState<CrmOportunidadListItem | null>(null);
  const [drawerDeal, setDrawerDeal] = useState<CrmOportunidadListItem | null>(null);
  const [nuevaOpen, setNuevaOpen] = useState(false);
  const [nuevaEtapaId, setNuevaEtapaId] = useState<number | null>(null);
  const [nuevaNombre, setNuevaNombre] = useState('');
  const [nuevaMonto, setNuevaMonto] = useState('');
  const [nuevaNroVendedor, setNuevaNroVendedor] = useState<number | null>(null);
  const [nuevaContactos, setNuevaContactos] = useState<CrmContactoListItem[]>([]);
  const [contactosOpts, setContactosOpts] = useState<CrmContactoListItem[]>([]);
  const [loadingContactos, setLoadingContactos] = useState(false);
  const [mostrarAltaContacto, setMostrarAltaContacto] = useState(false);
  const [nuevoContactoNombre, setNuevoContactoNombre] = useState('');
  const [nuevoContactoEmpresa, setNuevoContactoEmpresa] = useState('');
  const [nuevoContactoEmail, setNuevoContactoEmail] = useState('');
  const [nuevoContactoTelefono, setNuevoContactoTelefono] = useState('');
  const [savingContacto, setSavingContacto] = useState(false);
  const [savingNueva, setSavingNueva] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const loadPipelines = useCallback(async () => {
    try {
      const data = await crmPipelineService.getAll();
      const activos = (data ?? []).filter((p) => p.activo);
      setPipelines(activos);
      if (activos.length && pipelineId === '') {
        setPipelineId(activos[0].id);
      }
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al cargar pipelines'), { variant: 'error' });
    }
  }, [enqueueSnackbar, pipelineId]);

  const loadKanban = useCallback(async () => {
    if (pipelineId === '') return;
    try {
      setLoading(true);
      const data = await crmOportunidadService.getKanban({
        pipelineId,
        nroVendedor: nroVendedor ?? undefined,
        searchTerm: searchTerm.trim() || undefined,
      });
      setKanban(data);
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al cargar el kanban'), { variant: 'error' });
      setKanban(null);
    } finally {
      setLoading(false);
    }
  }, [pipelineId, nroVendedor, searchTerm, enqueueSnackbar]);

  useEffect(() => {
    loadPipelines();
  }, [loadPipelines]);

  useEffect(() => {
    const t = setTimeout(() => {
      loadKanban();
    }, 250);
    return () => clearTimeout(t);
  }, [loadKanban]);

  const columnas = useMemo(() => kanban?.columnas ?? [], [kanban]);

  const findDeal = (dealId: number): CrmOportunidadListItem | undefined => {
    for (const col of columnas) {
      const found = col.oportunidades.find((o) => o.id === dealId);
      if (found) return found;
    }
    return undefined;
  };

  const handleDragStart = (event: DragStartEvent) => {
    const deal = event.active.data.current?.deal as CrmOportunidadListItem | undefined;
    setActiveDeal(deal ?? null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveDeal(null);
    const { active, over } = event;
    if (!over) return;

    const deal = active.data.current?.deal as CrmOportunidadListItem | undefined;
    if (!deal) return;

    let targetEtapaId: number | null = null;
    const overId = String(over.id);
    if (overId.startsWith('etapa-')) {
      targetEtapaId = Number(overId.replace('etapa-', ''));
    } else if (over.data.current?.etapaId) {
      targetEtapaId = Number(over.data.current.etapaId);
    } else if (overId.startsWith('deal-')) {
      const overDealId = Number(overId.replace('deal-', ''));
      const overDeal = findDeal(overDealId);
      if (overDeal) targetEtapaId = overDeal.etapaId;
    }

    if (!targetEtapaId || targetEtapaId === deal.etapaId) return;

    const nuevaEtapaId = targetEtapaId;

    // Optimistic UI
    setKanban((prev) => {
      if (!prev) return prev;
      const nextCols = prev.columnas.map((col) => ({
        ...col,
        oportunidades: col.oportunidades.filter((o) => o.id !== deal.id),
      }));
      const target = nextCols.find((c) => c.etapa.id === nuevaEtapaId);
      if (target) {
        target.oportunidades = [
          { ...deal, etapaId: nuevaEtapaId, diasEnEtapa: 0, estancada: false },
          ...target.oportunidades,
        ];
      }
      return { ...prev, columnas: nextCols };
    });

    try {
      await crmOportunidadService.cambiarEtapa(deal.id, { etapaId: nuevaEtapaId });
      enqueueSnackbar('Etapa actualizada', { variant: 'success' });
      await loadKanban();
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo cambiar la etapa'), { variant: 'error' });
      await loadKanban();
    }
  };

  const resetNuevoContactoForm = () => {
    setNuevoContactoNombre('');
    setNuevoContactoEmpresa('');
    setNuevoContactoEmail('');
    setNuevoContactoTelefono('');
  };

  const openNuevaOportunidad = async (etapaId?: number) => {
    setNuevaEtapaId(etapaId ?? null);
    setNuevaOpen(true);
    setNuevaNombre('');
    setNuevaMonto('');
    setNuevaNroVendedor(nroVendedor);
    setNuevaContactos([]);
    setMostrarAltaContacto(false);
    resetNuevoContactoForm();
    try {
      setLoadingContactos(true);
      const page = await crmContactoService.getAll({
        pageNumber: 1,
        pageSize: 100,
        activo: true,
        nroVendedor: nroVendedor ?? undefined,
      });
      const items = page.items ?? [];
      setContactosOpts(items);
      if (items.length === 0) {
        setMostrarAltaContacto(true);
      }
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al cargar contactos'), { variant: 'error' });
      setContactosOpts([]);
      setMostrarAltaContacto(true);
    } finally {
      setLoadingContactos(false);
    }
  };

  const handleCrearContactoRapido = async () => {
    if (!nuevoContactoNombre.trim()) {
      enqueueSnackbar('Indicá el nombre del contacto', { variant: 'warning' });
      return;
    }
    if (nuevaNroVendedor == null) {
      enqueueSnackbar('Seleccioná el vendedor responsable', { variant: 'warning' });
      return;
    }
    setSavingContacto(true);
    try {
      const created = await crmContactoService.create({
        nombre: nuevoContactoNombre.trim(),
        empresa: nuevoContactoEmpresa.trim() || null,
        email: nuevoContactoEmail.trim() || null,
        telefono: nuevoContactoTelefono.trim() || null,
        nroVendedor: nuevaNroVendedor,
      });
      const asListItem: CrmContactoListItem = {
        id: created.id,
        nombre: created.nombre,
        empresa: created.empresa,
        cargo: created.cargo,
        telefono: created.telefono,
        email: created.email,
        nroVendedor: created.nroVendedor,
        nombreVendedor: created.nombreVendedor,
        nroCliente: created.nroCliente,
        nombreCliente: created.nombreCliente,
        origenId: created.origenId,
        origenNombre: created.origenNombre,
        activo: created.activo,
        fechaAlta: created.fechaAlta,
        tags: created.tags ?? [],
      };
      setContactosOpts((prev) => [asListItem, ...prev.filter((c) => c.id !== asListItem.id)]);
      setNuevaContactos((prev) => [...prev.filter((c) => c.id !== asListItem.id), asListItem]);
      resetNuevoContactoForm();
      setMostrarAltaContacto(false);
      enqueueSnackbar('Contacto creado y asociado', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al crear el contacto'), { variant: 'error' });
    } finally {
      setSavingContacto(false);
    }
  };

  const handleCreateOportunidad = async () => {
    if (pipelineId === '' || !nuevaNombre.trim()) {
      enqueueSnackbar('Indicá un nombre para la oportunidad', { variant: 'warning' });
      return;
    }
    if (nuevaNroVendedor == null) {
      enqueueSnackbar('Seleccioná el vendedor responsable', { variant: 'warning' });
      return;
    }
    if (nuevaContactos.length === 0) {
      enqueueSnackbar('Seleccioná o creá al menos un contacto', { variant: 'warning' });
      setMostrarAltaContacto(true);
      return;
    }
    setSavingNueva(true);
    try {
      const created = await crmOportunidadService.create({
        nombre: nuevaNombre.trim(),
        pipelineId,
        etapaId: nuevaEtapaId,
        nroVendedor: nuevaNroVendedor,
        montoEstimado: nuevaMonto ? Number(nuevaMonto) : null,
        usarSumaCotizaciones: !nuevaMonto,
        contactoIds: nuevaContactos.map((c) => c.id),
        contactoPrincipalId: nuevaContactos[0]?.id ?? null,
      });
      enqueueSnackbar('Oportunidad creada', { variant: 'success' });
      setNuevaOpen(false);
      setNuevaEtapaId(null);
      setNuevaNombre('');
      setNuevaMonto('');
      setNuevaNroVendedor(null);
      setNuevaContactos([]);
      setMostrarAltaContacto(false);
      resetNuevoContactoForm();
      navigate(ROUTES.crm.oportunidades.detalle(created.id));
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al crear oportunidad'), { variant: 'error' });
    } finally {
      setSavingNueva(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={2}
            >
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                Pipeline
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button startIcon={<RefreshIcon />} onClick={loadKanban} disabled={loading}>
                  Actualizar
                </Button>
                <Can permiso="crm.oportunidades.nuevo">
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => openNuevaOportunidad()}
                    disabled={pipelineId === ''}
                  >
                    Nueva oportunidad
                  </Button>
                </Can>
                <Can permiso="crm.pipeline">
                  <IconButton
                    color="primary"
                    aria-label="Configurar pipeline"
                    title="Configurar pipeline"
                    disabled={pipelineId === ''}
                    onClick={() =>
                      navigate(
                        `${ROUTES.crm.config}?editPipelineId=${encodeURIComponent(String(pipelineId))}`,
                      )
                    }
                  >
                    <SettingsIcon />
                  </IconButton>
                </Can>
              </Stack>
            </Stack>

            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                {pipelines.map((p) => {
                  const selected = pipelineId === p.id;
                  return (
                    <Chip
                      key={p.id}
                      label={p.nombre}
                      clickable
                      color={selected ? 'primary' : 'default'}
                      variant={selected ? 'filled' : 'outlined'}
                      onClick={() => setPipelineId(p.id)}
                      sx={{ fontWeight: selected ? 600 : 400 }}
                    />
                  );
                })}
                {pipelines.length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    No hay pipelines activos
                  </Typography>
                )}
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
                <TextField
                  size="small"
                  label="Buscar"
                  fullWidth
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{ flex: 1, minWidth: 200 }}
                />
                <VendedorSelect
                  value={nroVendedor}
                  onChange={setNroVendedor}
                  allowEmpty
                  emptyOptionLabel="Todos los vendedores"
                  size="small"
                  fullWidth={false}
                  sx={{ width: { xs: '100%', md: 220 }, minWidth: { md: 220 }, flexShrink: 0 }}
                />
              </Stack>
            </Stack>

            {loading && !kanban && (
              <Typography variant="body2" color="text.secondary">
                Cargando kanban...
              </Typography>
            )}

            <Box sx={{ overflowX: 'auto', pb: 1 }}>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCorners}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  {columnas.map((col) => (
                    <KanbanColumn
                      key={col.etapa.id}
                      columna={col}
                      onDealClick={(deal) => setDrawerDeal(deal)}
                      onAddClick={(etapaId) => openNuevaOportunidad(etapaId)}
                    />
                  ))}
                  {!loading && columnas.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No hay columnas para mostrar. Configurá un pipeline en CRM → Configuración.
                    </Typography>
                  )}
                </Stack>
                <DragOverlay>
                  {activeDeal ? <CrmDealCard deal={activeDeal} dragDisabled /> : null}
                </DragOverlay>
              </DndContext>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Drawer
        anchor="right"
        open={Boolean(drawerDeal)}
        onClose={() => setDrawerDeal(null)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 380 },
            // El AppBar es fixed y queda por encima del drawer (zIndex mayor)
            top: { xs: 56, sm: 64 },
            height: { xs: 'calc(100% - 56px)', sm: 'calc(100% - 64px)' },
            p: 2,
            boxSizing: 'border-box',
          },
        }}
      >
        {drawerDeal && (
          <Stack spacing={2}>
            <Box>
              <Typography
                variant="h5"
                component="h2"
                sx={{
                  fontWeight: 700,
                  color: '#1976d2',
                  lineHeight: 1.3,
                  wordBreak: 'break-word',
                }}
              >
                {drawerDeal.nombre?.trim() ||
                  drawerDeal.contactoPrincipal ||
                  `Oportunidad #${drawerDeal.id}`}
              </Typography>
              <Typography variant="h6" color="text.primary" sx={{ fontWeight: 600, mt: 0.75 }}>
                {formatCurrency(drawerDeal.monto)}
              </Typography>
            </Box>
            <Typography variant="body2">Etapa: {drawerDeal.etapaNombre}</Typography>
            <Typography variant="body2">
              Contacto: {drawerDeal.contactoPrincipal || '-'}
            </Typography>
            <Typography variant="body2">
              Vendedor: {drawerDeal.nombreVendedor || drawerDeal.nroVendedor}
            </Typography>
            <Typography variant="body2">
              Días en etapa: {drawerDeal.diasEnEtapa}
              {drawerDeal.estancada ? ' (estancada)' : ''}
            </Typography>
            <Typography variant="body2">Estado: {drawerDeal.estado}</Typography>
            <Button
              variant="contained"
              onClick={() => navigate(ROUTES.crm.oportunidades.detalle(drawerDeal.id))}
            >
              Ver detalle
            </Button>
          </Stack>
        )}
      </Drawer>

      <Dialog
        open={nuevaOpen}
        onClose={() => {
          setNuevaOpen(false);
          setNuevaEtapaId(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Nueva oportunidad
          {nuevaEtapaId != null && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 400 }}>
              Etapa inicial:{' '}
              {columnas.find((c) => c.etapa.id === nuevaEtapaId)?.etapa.nombre ?? 'seleccionada'}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Nombre"
              required
              fullWidth
              value={nuevaNombre}
              onChange={(e) => setNuevaNombre(e.target.value)}
            />
            <VendedorSelect
              value={nuevaNroVendedor}
              onChange={setNuevaNroVendedor}
              label="Vendedor responsable"
              required
              fullWidth
              size="small"
              helperText="Obligatorio para crear el contacto y la oportunidad"
            />
            <Stack spacing={1}>
              <Autocomplete
                multiple
                options={contactosOpts}
                loading={loadingContactos}
                value={nuevaContactos}
                onChange={(_, value) => setNuevaContactos(value)}
                getOptionLabel={(o) =>
                  o.empresa ? `${o.nombre} (${o.empresa})` : o.nombre
                }
                isOptionEqualToValue={(a, b) => a.id === b.id}
                noOptionsText={
                  loadingContactos
                    ? 'Cargando...'
                    : 'No hay contactos. Creá uno abajo.'
                }
                renderInput={(params) => (
                  <TextField {...params} label="Contactos" required helperText="Al menos uno" />
                )}
              />
              <Box>
                <Button
                  size="small"
                  startIcon={<PersonAddIcon />}
                  onClick={() => setMostrarAltaContacto((v) => !v)}
                >
                  {mostrarAltaContacto ? 'Ocultar alta de contacto' : 'Nuevo contacto'}
                </Button>
              </Box>
              {mostrarAltaContacto && (
                <Stack
                  spacing={1.5}
                  sx={{
                    p: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Alta rápida de contacto
                  </Typography>
                  <TextField
                    label="Nombre del contacto"
                    required
                    fullWidth
                    size="small"
                    value={nuevoContactoNombre}
                    onChange={(e) => setNuevoContactoNombre(e.target.value)}
                  />
                  <TextField
                    label="Empresa"
                    fullWidth
                    size="small"
                    value={nuevoContactoEmpresa}
                    onChange={(e) => setNuevoContactoEmpresa(e.target.value)}
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <TextField
                      label="Email"
                      fullWidth
                      size="small"
                      value={nuevoContactoEmail}
                      onChange={(e) => setNuevoContactoEmail(e.target.value)}
                    />
                    <TextField
                      label="Teléfono"
                      fullWidth
                      size="small"
                      value={nuevoContactoTelefono}
                      onChange={(e) => setNuevoContactoTelefono(e.target.value)}
                    />
                  </Stack>
                  <Box>
                    <Can permiso="crm.contactos.nuevo">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<PersonAddIcon />}
                        onClick={handleCrearContactoRapido}
                        disabled={
                          savingContacto ||
                          !nuevoContactoNombre.trim() ||
                          nuevaNroVendedor == null
                        }
                      >
                        {savingContacto ? 'Guardando...' : 'Crear y asociar contacto'}
                      </Button>
                    </Can>
                  </Box>
                </Stack>
              )}
            </Stack>
            <TextField
              label="Monto estimado"
              type="number"
              fullWidth
              value={nuevaMonto}
              onChange={(e) => setNuevaMonto(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNuevaOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleCreateOportunidad}
            disabled={savingNueva || nuevaContactos.length === 0 || nuevaNroVendedor == null}
          >
            Crear
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CrmPipelineKanban;
